import setWith from 'lodash/setWith';
import get from 'lodash/get';

import {SortBy, SortOrder} from 'common/charts/visualization/bar-viz/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {LineDataTableFields} from 'common/widgets/trend-data-table/utils';

import {
  CellRenderContext,
  ColSortConfig,
  ColSortOptionValue,
  SEGMENT_PREFIX,
  SegmentStats,
  SortByValueConfig,
  SortTransformFunc,
  TableColumn,
  TableRow,
  TableSortConfig,
} from './types';

export function rowSortComparator(
  a: TableRow,
  b: TableRow,
  {
    sortOrder,
    sortTransform,
    sortField,
  }: {
    sortOrder: number;
    sortTransform?: SortTransformFunc<TableRow>;
    sortField?: string;
  },
): number {
  if (sortTransform) {
    return sortOrder * (sortTransform(a, sortField, {sortOrder}) < sortTransform(b, sortField, {sortOrder}) ? -1 : 1);
  }

  if (sortField) {
    const aVal = a[sortField]; // assume flat structure
    const bVal = b[sortField]; // assume flat structure
    const aType = typeof aVal;
    const bType = typeof bVal;

    if (aType === bType) {
      if (aType === `string`) {
        return sortOrder * aVal.localeCompare(bVal);
      } else if (aType === `number` || aType === `boolean`) {
        return sortOrder * (aVal - bVal);
      }
    }

    // truthy values come before falsey values
    return (aVal ? 0 : 1) - (bVal ? 0 : 1);
  }

  return 0;
}

export function createRowCompareFunc({
  columns,
  sortConfig,
  rows,
}: {
  columns: TableColumn[];
  sortConfig: TableSortConfig;
  rows: TableRow[];
}): (a: TableRow, b: TableRow) => number {
  if (sortConfig.sortBy === SortBy.Value) {
    // do a single rowSortComparator sort (flat sort) as before
    const sortField = sortConfig.valueField;
    const sortTransform = columns.find((col) => col.field === sortField)?.sortTransform;
    const sortOrder = numericSortOrder(sortConfig.sortOrder);
    return (a, b) => rowSortComparator(a, b, {sortOrder, sortTransform, sortField});
  } else {
    const numSegments = getNumSegments(columns);
    if (numSegments < 2) {
      // if numSegments is 1 just do a flat sort
      const colSortConfig = sortConfig.colSortAttrs[0];
      // TODO(j) we should not allow SortBy.Value with 1 segment. dropdown should just be label sort or header click
      const sortField = colSortConfig.sortBy === SortBy.Value ? colSortConfig.valueField : `${SEGMENT_PREFIX}0`;
      const sortTransform = columns.find((col) => col.field === sortField)?.sortTransform;
      const sortOrder = numericSortOrder(colSortConfig.sortOrder);
      return (a, b) => rowSortComparator(a, b, {sortOrder, sortTransform, sortField});
    } else {
      return createMultiSegRowCompareFunc({columns, rows, sortConfig, numSegments});
    }
  }
}

function createMultiSegRowCompareFunc({columns, rows, sortConfig, numSegments}) {
  const segmentStats = getSegmentStats({columns, rows, sortConfig, numSegments});

  return (a, b) => {
    // Compare rows at successive segmentation levels until comparison result !== 0
    const aPath = [];
    const bPath = [];
    let result = 0;
    for (let i = 0; i < numSegments; i++) {
      const segmentField = `${SEGMENT_PREFIX}${i}`;
      const aSegmentValue = a[segmentField];
      const bSegmentValue = b[segmentField];

      pushSegmentPath(aPath, aSegmentValue, i === 0);
      pushSegmentPath(bPath, bSegmentValue, i === 0);
      if (aSegmentValue === bSegmentValue) {
        // Nothing to sort when segments are the same, continue to next segmentation level
        continue;
      }

      const colSortConfig = sortConfig.colSortAttrs[i];
      const sortOrder = numericSortOrder(colSortConfig?.sortOrder);
      const labelCompare = !colSortConfig || colSortConfig.sortBy === SortBy.Label;
      if (labelCompare) {
        // TODO(mack) or TODO(j): apply sortTransform here (needed for ordering numeric range segments)
        result = sortOrder * aSegmentValue.localeCompare(bSegmentValue);
      } else {
        // Value sort. Retrieve the segments' aggregated values and compare those.
        // @ts-ignore The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
        result = sortOrder * (get(segmentStats, [...aPath, `sum`]) - get(segmentStats, [...bPath, `sum`]));

        if (result === 0) {
          // Alpha sort if the segments have same sum
          result = sortOrder * aSegmentValue.localeCompare(bSegmentValue);
        }
      }

      if (result !== 0) {
        break;
      }
    }
    return result;
  };
}

function getSegmentStats({
  columns,
  rows,
  sortConfig,
  numSegments,
}: {
  columns: TableColumn[];
  rows: TableRow[];
  sortConfig: TableSortConfig;
  numSegments: number;
}): SegmentStats {
  return rows.reduce((accum, row) => {
    const path = [];
    for (let i = 0; i < numSegments; i++) {
      const segmentValue = row[`${SEGMENT_PREFIX}${i}`];

      pushSegmentPath(path, segmentValue, i === 0);

      const colSortConfig = sortConfig.colSortAttrs[i];
      const {sortBy, sortOrder, valueField} = colSortConfig;
      let value: number;
      if (sortBy === SortBy.Value) {
        const sortTransform = columns.find((col) => col.field === valueField)?.sortTransform;
        value = sortTransform
          ? sortTransform(row, valueField, {sortOrder: numericSortOrder(sortOrder)})
          : row[valueField];
      } else {
        // no need to retrieve value for label sort-- the segment will be sorted alphabetically
        value = 0;
      }

      setWith(accum, [...path, `sum`], get(accum, [...path, `sum`], 0) + value, Object);
    }
    return accum;
  }, {});
}

export function getNumSegments(columns: TableColumn[]) {
  return columns.filter((col) => col.field.startsWith(SEGMENT_PREFIX)).length;
}

/** Utility for starting / extending a path for use in a SegmentStats tree, e.g. ["Chrome", "children", "USA"] */
function pushSegmentPath(path: string[], segment: string, isFirst: boolean): void {
  if (!isFirst) {
    path.push(`children`);
  }
  path.push(segment);
}

export function invertSortOrder(sortOrder: SortOrder) {
  return sortOrder === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending;
}

function numericSortOrder(sortOrder: SortOrder) {
  return sortOrder === SortOrder.Ascending ? 1 : -1;
}

function enumSortOrder(sortOrder: number) {
  return sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending;
}

export function convertOldSortingConfig({
  sortField,
  sortOrder,
}: {
  sortField: string;
  sortOrder: number;
}): SortByValueConfig {
  return {
    sortBy: SortBy.Value,
    sortOrder: enumSortOrder(sortOrder),
    valueField: sortField,
    colSortAttrs: [],
  };
}

/** Adds missing column sort configs in `sorting.colSortAttrs` and truncates
 * `sorting.colSortAttrs` as needed so its length matches the number of segments */
export function fillMissingColSortAttrs({
  columns,
  sorting,
  defaultColSortConfig = {sortBy: SortBy.Label, sortOrder: SortOrder.Ascending},
}: {
  columns: TableColumn[];
  sorting: TableSortConfig;
  defaultColSortConfig?: ColSortConfig;
}): TableSortConfig {
  const numSegments = getNumSegments(columns);
  const colSortAttrs = [];

  for (let i = 0; i < numSegments; i++) {
    const config = sorting.colSortAttrs[i];
    colSortAttrs[i] = config ? {...config} : {...defaultColSortConfig};
  }

  return {...sorting, colSortAttrs};
}

function getColSortOptionValue({
  segmentIndex,
  sortOrder,
  valueField,
}: {
  segmentIndex: number;
  sortOrder: SortOrder;
  valueField: string;
}): ColSortOptionValue {
  return {
    segmentIndex,
    sort: {sortBy: SortBy.Value, sortOrder, valueField},
  };
}

function getValueSortSelectSections({
  colSortConfig,
  segmentIndex,
  sortOrder,
  sortingByColumns,
  columns,
}: {
  colSortConfig: ColSortConfig;
  segmentIndex: number;
  sortOrder: SortOrder;
  sortingByColumns: boolean;
  columns: TableColumn[];
}): Section[] {
  const sectionItems = columns.reduce((accum, col) => {
    if (col.title && !col.field.startsWith(SEGMENT_PREFIX)) {
      const value = getColSortOptionValue({segmentIndex, sortOrder, valueField: col.field});
      accum.push({
        icon: `type-${col.type}`,
        label: col.title,
        value,
        isSelected: sortingByColumns && colSortConfig?.valueField === col.field,
      });
    }
    return accum;
  }, []);
  return [{items: sectionItems}];
}

export function getSortSelectValueItems({
  colSortConfig,
  segmentIndex,
  sortingByColumns,
  columns,
}: {
  colSortConfig: ColSortConfig;
  segmentIndex: number;
  sortingByColumns: boolean;
  columns: TableColumn[];
}): SectionItem[] {
  const ascValueSelected =
    sortingByColumns && colSortConfig?.sortBy === SortBy.Value && colSortConfig?.sortOrder === SortOrder.Ascending;
  const descValueSelected =
    sortingByColumns && colSortConfig?.sortBy === SortBy.Value && colSortConfig?.sortOrder === SortOrder.Descending;

  let valueItems: SectionItem[];
  const onlyShowAverage =
    columns.some((col) => col.field === LineDataTableFields.Average) &&
    columns.some((col) => col.field.startsWith(LineDataTableFields.DatePointPrefix));
  if (onlyShowAverage) {
    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `Average 1 → 100`,
        isSelected: ascValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Ascending,
          valueField: LineDataTableFields.Average,
        }),
      },
      {
        icon: `sort-value-desc`,
        label: `Average 100 → 1`,
        isSelected: descValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Descending,
          valueField: LineDataTableFields.Average,
        }),
      },
    ];
  } else {
    const ascValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Ascending,
      sortingByColumns,
      columns,
    });
    const descValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Descending,
      sortingByColumns,
      columns,
    });

    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `Value 1 → 100`,
        subSections: ascValueSubsections,
        isSelected: ascValueSelected,
      },
      {
        icon: `sort-value-desc`,
        label: `Value 100 → 1`,
        subSections: descValueSubsections,
        isSelected: descValueSelected,
      },
    ];
  }
  return valueItems;
}

export function isSameRowByContext(contextA: CellRenderContext, contextB: CellRenderContext) {
  return Boolean(
    contextA && contextB && contextA.rowIdx === contextB.rowIdx && contextA.groupedRowIdx === contextB.groupedRowIdx,
  );
}
